import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  AlertTriangle,
  ArrowRightLeft,
  BarChart3,
  CalendarClock,
  CheckCircle2,
  Clock,
  MapPin,
  PoundSterling,
  Route,
  Sparkles,
  Users,
} from 'lucide-react';
import { localCaregivers, localClients, localVisits } from '@/core/services/local-api-fallback';

type ForecastPeriod = 'day' | 'week' | 'month';
type AlertLevel = 'high' | 'medium' | 'low';

interface Coordinates {
  lat: number;
  lon: number;
}

interface CarerProfile {
  id: string;
  name: string;
  email: string;
  hourlyRate: number;
  home: Coordinates;
  skills: string[];
  availableDays: number[];
  preferredClients: string[];
  maxWeeklyHours: number;
}

interface OptimizerVisit {
  id: string;
  visitNumber: string;
  clientId: string;
  clientName: string;
  serviceType: string;
  scheduledDate: string;
  scheduledStartTime: string;
  durationMinutes: number;
  assignedCaregiverId: string;
  currentCaregiverName: string;
  requiredSkills: string[];
  coordinates: Coordinates;
  status: string;
}

interface CandidateMatch {
  caregiverId: string;
  caregiverName: string;
  score: number;
  distanceMiles: number;
  travelMinutes: number;
  estimatedCost: number;
  overtimeHours: number;
  weeklyHoursAfter: number;
  hasOverlap: boolean;
  isAvailable: boolean;
  skillMatch: boolean;
  reasons: string[];
  warnings: string[];
}

interface AssignmentPlan {
  visit: OptimizerVisit;
  currentCandidate: CandidateMatch | null;
  recommendedCandidate: CandidateMatch;
  savings: number;
}

interface CostAlert {
  id: string;
  level: AlertLevel;
  title: string;
  detail: string;
}

const clientCoordinates: Record<string, Coordinates> = {
  'client-1': { lat: 52.6082, lon: 1.7263 },
  'client-2': { lat: 52.6124, lon: 1.7048 },
  'client-3': { lat: 52.6041, lon: 1.7314 },
  'client-4': { lat: 52.6117, lon: 1.7247 },
  'client-5': { lat: 52.6028, lon: 1.7378 },
};

const carerProfiles: CarerProfile[] = localCaregivers.map((caregiver, index) => {
  const homes: Coordinates[] = [
    { lat: 52.5868, lon: 1.7235 },
    { lat: 52.6192, lon: 1.7082 },
    { lat: 52.6039, lon: 1.7444 },
    { lat: 52.5723, lon: 1.7196 },
    { lat: 52.6286, lon: 1.7332 },
  ];
  const skillSets = [
    ['Personal Care', 'Medication Support', 'Meal Preparation'],
    ['Companionship', 'Dementia Support', 'Personal Care'],
    ['Dementia Support', 'Medication Support', 'Personal Care'],
    ['Medication Support', 'Supervision', 'Night Care'],
    ['Meal Preparation', 'Companionship', 'Personal Care'],
  ];

  return {
    id: caregiver.id,
    name: caregiver.fullName,
    email: caregiver.email,
    hourlyRate: [14.5, 15.25, 14.75, 16, 15][index] ?? 15,
    home: homes[index] ?? homes[0]!,
    skills: skillSets[index] ?? ['Personal Care'],
    availableDays: index === 1 ? [1, 2, 3, 4] : index === 3 ? [1, 2, 3, 4, 5, 6] : [1, 2, 3, 4, 5],
    preferredClients: index === 0 ? ['client-1'] : index === 2 ? ['client-3'] : index === 4 ? ['client-5'] : [],
    maxWeeklyHours: index === 3 ? 32 : 40,
  };
});

const currency = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });

function toIsoDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function addDays(dateString: string, days: number): string {
  const date = new Date(`${dateString}T00:00:00`);
  date.setDate(date.getDate() + days);
  return toIsoDate(date);
}

function getWeekStart(dateString: string): string {
  const date = new Date(`${dateString}T00:00:00`);
  const day = date.getDay() || 7;
  date.setDate(date.getDate() - day + 1);
  return toIsoDate(date);
}

function isWithinPeriod(dateString: string, selectedDate: string, period: ForecastPeriod): boolean {
  if (period === 'day') return dateString === selectedDate;
  if (period === 'week') return getWeekStart(dateString) === getWeekStart(selectedDate);
  return dateString.slice(0, 7) === selectedDate.slice(0, 7);
}

function timeToMinutes(value: string): number {
  const [hours, minutes] = value.split(':').map(Number);
  return (hours ?? 0) * 60 + (minutes ?? 0);
}

function visitStartMinutes(visit: OptimizerVisit): number {
  return timeToMinutes(visit.scheduledStartTime);
}

function visitEndMinutes(visit: OptimizerVisit): number {
  return visitStartMinutes(visit) + visit.durationMinutes;
}

function overlaps(a: OptimizerVisit, b: OptimizerVisit, travelBufferMinutes = 15): boolean {
  if (a.id === b.id || a.scheduledDate !== b.scheduledDate) return false;
  return visitStartMinutes(a) < visitEndMinutes(b) + travelBufferMinutes
    && visitEndMinutes(a) + travelBufferMinutes > visitStartMinutes(b);
}

function distanceMiles(a: Coordinates, b: Coordinates): number {
  const earthRadiusMiles = 3958.8;
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const deltaLat = ((b.lat - a.lat) * Math.PI) / 180;
  const deltaLon = ((b.lon - a.lon) * Math.PI) / 180;
  const h =
    Math.sin(deltaLat / 2) ** 2
    + Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLon / 2) ** 2;
  return earthRadiusMiles * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

function travelMinutesFromMiles(miles: number): number {
  return Math.round((miles / 24) * 60 + 6);
}

function requiredSkillsFor(serviceType: string): string[] {
  if (serviceType.toLowerCase().includes('medication')) return ['Medication Support'];
  if (serviceType.toLowerCase().includes('dementia')) return ['Dementia Support'];
  if (serviceType.toLowerCase().includes('companionship')) return ['Companionship'];
  if (serviceType.toLowerCase().includes('meal')) return ['Meal Preparation'];
  return ['Personal Care'];
}

function normalizeVisits(assignments: Record<string, string>): OptimizerVisit[] {
  return localVisits.map((visit) => {
    const clientId = visit.clientId;
    const serviceType = visit.serviceTypeName ?? visit.serviceType ?? 'Personal Care';
    const assignedCaregiverId = assignments[visit.id] ?? visit.assignedCaregiverId ?? carerProfiles[0]!.id;
    const currentCaregiver = localCaregivers.find((caregiver) => caregiver.id === assignedCaregiverId);

    return {
      id: visit.id,
      visitNumber: visit.visitNumber,
      clientId,
      clientName: visit.clientName,
      serviceType,
      scheduledDate: visit.scheduledDate,
      scheduledStartTime: visit.scheduledStartTime,
      durationMinutes: visit.scheduledDuration,
      assignedCaregiverId,
      currentCaregiverName: currentCaregiver?.fullName ?? 'Unassigned',
      requiredSkills: requiredSkillsFor(serviceType),
      coordinates: clientCoordinates[clientId] ?? { lat: 52.5868, lon: 1.7235 },
      status: visit.status,
    };
  });
}

function getWeeklyHours(visits: OptimizerVisit[], caregiverId: string, dateString: string): number {
  const weekStart = getWeekStart(dateString);
  return visits
    .filter((visit) => visit.assignedCaregiverId === caregiverId && getWeekStart(visit.scheduledDate) === weekStart)
    .reduce((total, visit) => total + visit.durationMinutes / 60, 0);
}

function evaluateCandidate(visit: OptimizerVisit, caregiver: CarerProfile, allVisits: OptimizerVisit[]): CandidateMatch {
  const miles = distanceMiles(caregiver.home, visit.coordinates);
  const travelMinutes = travelMinutesFromMiles(miles);
  const visitHours = visit.durationMinutes / 60;
  const travelHours = travelMinutes / 60;
  const day = new Date(`${visit.scheduledDate}T00:00:00`).getDay() || 7;
  const isAvailable = caregiver.availableDays.includes(day);
  const skillMatch = visit.requiredSkills.every((skill) => caregiver.skills.includes(skill));
  const hasOverlap = allVisits
    .filter((item) => item.assignedCaregiverId === caregiver.id)
    .some((item) => overlaps(visit, item));
  const weeklyHoursBefore = getWeeklyHours(allVisits, caregiver.id, visit.scheduledDate);
  const weeklyHoursAfter = weeklyHoursBefore + visitHours;
  const overtimeHours = Math.max(0, weeklyHoursAfter - caregiver.maxWeeklyHours);
  const overtimeCost = overtimeHours > 0 ? overtimeHours * caregiver.hourlyRate * 0.5 : 0;
  const estimatedCost = visitHours * caregiver.hourlyRate + travelHours * caregiver.hourlyRate + miles * 0.45 + overtimeCost;

  const proximityScore = Math.max(0, 25 - miles * 2);
  const skillScore = skillMatch ? 20 : 4;
  const availabilityScore = isAvailable ? 18 : 0;
  const continuityScore = caregiver.preferredClients.includes(visit.clientId) ? 12 : caregiver.id === visit.assignedCaregiverId ? 8 : 4;
  const workloadScore = overtimeHours === 0 ? Math.max(4, 15 - Math.max(0, weeklyHoursAfter - 32)) : 0;
  const conflictScore = hasOverlap ? 0 : 15;
  const score = Math.round(proximityScore + skillScore + availabilityScore + continuityScore + workloadScore + conflictScore);

  const reasons = [
    `${miles.toFixed(1)} miles from carer base, about ${travelMinutes} minutes travel`,
    skillMatch ? 'Required skills match' : `Missing skill: ${visit.requiredSkills.filter((skill) => !caregiver.skills.includes(skill)).join(', ')}`,
    isAvailable ? 'Available on this day' : 'Not normally available on this day',
    overtimeHours === 0 ? `No overtime risk, ${weeklyHoursAfter.toFixed(1)} weekly hours after assignment` : `${overtimeHours.toFixed(1)} overtime hours risk`,
  ];

  const warnings = [
    !isAvailable && 'Carer is not marked available for this day',
    hasOverlap && 'Overlapping shift or insufficient travel buffer',
    overtimeHours > 0 && 'Assignment creates overtime cost',
    miles > 10 && 'Long travel distance may add unnecessary cost',
    !skillMatch && 'Skill mismatch requires manager approval',
  ].filter(Boolean) as string[];

  return {
    caregiverId: caregiver.id,
    caregiverName: caregiver.name,
    score,
    distanceMiles: miles,
    travelMinutes,
    estimatedCost,
    overtimeHours,
    weeklyHoursAfter,
    hasOverlap,
    isAvailable,
    skillMatch,
    reasons,
    warnings,
  };
}

function bestCandidateFor(visit: OptimizerVisit, visits: OptimizerVisit[]): CandidateMatch {
  const candidates = carerProfiles
    .map((caregiver) => evaluateCandidate(visit, caregiver, visits))
    .sort((a, b) => {
      const aBlocking = Number(a.hasOverlap || !a.isAvailable || !a.skillMatch);
      const bBlocking = Number(b.hasOverlap || !b.isAvailable || !b.skillMatch);
      return aBlocking - bBlocking || b.score - a.score || a.estimatedCost - b.estimatedCost;
    });

  return candidates[0]!;
}

function calculatePlans(visits: OptimizerVisit[]): AssignmentPlan[] {
  return visits.map((visit) => {
    const currentProfile = carerProfiles.find((caregiver) => caregiver.id === visit.assignedCaregiverId);
    const currentCandidate = currentProfile ? evaluateCandidate(visit, currentProfile, visits) : null;
    const recommendedCandidate = bestCandidateFor(visit, visits);

    return {
      visit,
      currentCandidate,
      recommendedCandidate,
      savings: Math.max(0, (currentCandidate?.estimatedCost ?? recommendedCandidate.estimatedCost) - recommendedCandidate.estimatedCost),
    };
  });
}

function buildCostAlerts(plans: AssignmentPlan[]): CostAlert[] {
  const expensiveAssignments = plans.filter((plan) => plan.savings > 5);
  const overlapPlans = plans.filter((plan) => plan.currentCandidate?.hasOverlap);
  const overtimePlans = plans.filter((plan) => (plan.currentCandidate?.overtimeHours ?? 0) > 0);
  const longTravelPlans = plans.filter((plan) => (plan.currentCandidate?.distanceMiles ?? 0) > 10);

  return [
    ...expensiveAssignments.map((plan) => ({
      id: `cost-${plan.visit.id}`,
      level: 'medium' as const,
      title: `Cost saving available for ${plan.visit.clientName}`,
      detail: `Switching to ${plan.recommendedCandidate.caregiverName} may save ${currency.format(plan.savings)} for this visit.`,
    })),
    ...overlapPlans.map((plan) => ({
      id: `overlap-${plan.visit.id}`,
      level: 'high' as const,
      title: `Overlapping shift detected for ${plan.visit.currentCaregiverName}`,
      detail: `${plan.visit.visitNumber} needs review before rota confirmation.`,
    })),
    ...overtimePlans.map((plan) => ({
      id: `overtime-${plan.visit.id}`,
      level: 'high' as const,
      title: `Overtime risk on ${plan.visit.visitNumber}`,
      detail: `${plan.visit.currentCaregiverName} may exceed weekly capacity.`,
    })),
    ...longTravelPlans.map((plan) => ({
      id: `travel-${plan.visit.id}`,
      level: 'low' as const,
      title: `Long travel route for ${plan.visit.clientName}`,
      detail: `${plan.currentCandidate?.distanceMiles.toFixed(1)} miles estimated from carer base.`,
    })),
  ];
}

const alertStyles: Record<AlertLevel, string> = {
  high: 'bg-red-100 text-red-800 border-red-200',
  medium: 'bg-amber-100 text-amber-800 border-amber-200',
  low: 'bg-blue-100 text-blue-800 border-blue-200',
};

export const ScheduleOptimizer: React.FC = () => {
  const [forecastPeriod, setForecastPeriod] = useState<ForecastPeriod>('week');
  const [selectedDate, setSelectedDate] = useState(toIsoDate(new Date()));
  const [assignments, setAssignments] = useState<Record<string, string>>({});
  const [selectedVisitId, setSelectedVisitId] = useState(localVisits[0]?.id ?? '');

  const allVisits = useMemo(() => normalizeVisits(assignments), [assignments]);
  const periodVisits = useMemo(
    () => allVisits.filter((visit) => isWithinPeriod(visit.scheduledDate, selectedDate, forecastPeriod)),
    [allVisits, selectedDate, forecastPeriod]
  );
  const plans = useMemo(() => calculatePlans(periodVisits), [periodVisits]);
  const alerts = useMemo(() => buildCostAlerts(plans), [plans]);
  const selectedPlan = plans.find((plan) => plan.visit.id === selectedVisitId) ?? plans[0];
  const selectedCandidates = useMemo(() => {
    if (!selectedPlan) return [];
    return carerProfiles
      .map((caregiver) => evaluateCandidate(selectedPlan.visit, caregiver, periodVisits))
      .sort((a, b) => b.score - a.score || a.estimatedCost - b.estimatedCost);
  }, [periodVisits, selectedPlan]);

  const currentCost = plans.reduce((total, plan) => total + (plan.currentCandidate?.estimatedCost ?? plan.recommendedCandidate.estimatedCost), 0);
  const optimizedCost = plans.reduce((total, plan) => total + plan.recommendedCandidate.estimatedCost, 0);
  const totalSavings = Math.max(0, currentCost - optimizedCost);
  const currentTravel = plans.reduce((total, plan) => total + (plan.currentCandidate?.travelMinutes ?? plan.recommendedCandidate.travelMinutes), 0);
  const optimizedTravel = plans.reduce((total, plan) => total + plan.recommendedCandidate.travelMinutes, 0);
  const overlapCount = plans.filter((plan) => plan.currentCandidate?.hasOverlap).length;
  const overtimeCount = plans.filter((plan) => (plan.currentCandidate?.overtimeHours ?? 0) > 0).length;

  const utilisation = carerProfiles.map((caregiver) => {
    const hours = periodVisits
      .filter((visit) => visit.assignedCaregiverId === caregiver.id)
      .reduce((total, visit) => total + visit.durationMinutes / 60, 0);
    const periodCapacity = forecastPeriod === 'day' ? 8 : forecastPeriod === 'week' ? caregiver.maxWeeklyHours : caregiver.maxWeeklyHours * 4;
    return {
      caregiver,
      hours,
      utilisation: periodCapacity > 0 ? Math.round((hours / periodCapacity) * 100) : 0,
    };
  });
  const underutilized = utilisation.filter((item) => item.utilisation < 45);

  const applyRecommendation = (plan: AssignmentPlan) => {
    setAssignments((current) => ({ ...current, [plan.visit.id]: plan.recommendedCandidate.caregiverId }));
    toast.success(`${plan.recommendedCandidate.caregiverName} assigned to ${plan.visit.clientName}`);
  };

  const autoOptimise = () => {
    const nextAssignments = { ...assignments };
    plans.forEach((plan) => {
      nextAssignments[plan.visit.id] = plan.recommendedCandidate.caregiverId;
    });
    setAssignments(nextAssignments);
    toast.success('Rota optimised for the selected period');
  };

  const resetOptimisation = () => {
    setAssignments({});
    toast.success('Optimisation reset to current rota');
  };

  return (
    <div className="space-y-8">
      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex items-center gap-5">
            <div className="rounded-lg bg-[#6c3a80]/10 p-4 text-[#6c3a80]">
              <Sparkles className="h-8 w-8" />
            </div>
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-[#6c3a80]">Money-saving scheduling</p>
              <h1 className="mt-1 text-3xl font-bold text-slate-950">AI Schedule Optimizer</h1>
              <p className="mt-2 max-w-4xl text-sm leading-6 text-slate-600">
                Recommend the best carer for each visit by distance, skills, availability, continuity, overtime risk, travel time and estimated cost before rota confirmation.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={autoOptimise}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-[#6c3a80] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#5e3474]"
            >
              <Sparkles className="h-4 w-4" />
              Auto optimise rota
            </button>
            <button
              type="button"
              onClick={resetOptimisation}
              className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              <ArrowRightLeft className="h-4 w-4" />
              Reset
            </button>
          </div>
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <div className="grid gap-4 md:grid-cols-3">
          <label className="space-y-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Forecast period</span>
            <select
              value={forecastPeriod}
              onChange={(event) => setForecastPeriod(event.target.value as ForecastPeriod)}
              className="h-11 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#6c3a80] focus:ring-2 focus:ring-[#6c3a80]/20"
            >
              <option value="day">Daily</option>
              <option value="week">Weekly</option>
              <option value="month">Monthly</option>
            </select>
          </label>
          <label className="space-y-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Selected date</span>
            <input
              type="date"
              value={selectedDate}
              onChange={(event) => setSelectedDate(event.target.value)}
              className="h-11 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#6c3a80] focus:ring-2 focus:ring-[#6c3a80]/20"
            />
          </label>
          <div className="rounded-md bg-slate-50 p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Rota scope</p>
            <p className="mt-1 text-2xl font-bold text-slate-950">{periodVisits.length} visits</p>
          </div>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-slate-500">Estimated saving</p>
            <PoundSterling className="h-5 w-5 text-emerald-700" />
          </div>
          <p className="mt-3 text-3xl font-bold text-slate-950">{currency.format(totalSavings)}</p>
          <p className="mt-1 text-sm text-slate-500">{forecastPeriod} forecast before rota confirmation</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-slate-500">Travel saved</p>
            <Route className="h-5 w-5 text-blue-700" />
          </div>
          <p className="mt-3 text-3xl font-bold text-slate-950">{Math.max(0, currentTravel - optimizedTravel)} min</p>
          <p className="mt-1 text-sm text-slate-500">based on carer base to client route</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-slate-500">Overlap risks</p>
            <CalendarClock className="h-5 w-5 text-red-700" />
          </div>
          <p className="mt-3 text-3xl font-bold text-slate-950">{overlapCount}</p>
          <p className="mt-1 text-sm text-slate-500">shift conflicts detected</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-slate-500">Underutilized carers</p>
            <Users className="h-5 w-5 text-[#6c3a80]" />
          </div>
          <p className="mt-3 text-3xl font-bold text-slate-950">{underutilized.length}</p>
          <p className="mt-1 text-sm text-slate-500">available for extra work</p>
        </div>
      </section>

      <section className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-xl font-bold text-slate-950">Visit recommendations</h2>
              <p className="mt-1 text-sm text-slate-600">Closest available carer with matching skills is recommended first.</p>
            </div>
            <BarChart3 className="h-6 w-6 text-[#6c3a80]" />
          </div>
          <div className="mt-5 overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-3">Visit</th>
                  <th className="px-3 py-3">Current</th>
                  <th className="px-3 py-3">Recommended</th>
                  <th className="px-3 py-3">Travel</th>
                  <th className="px-3 py-3">Saving</th>
                  <th className="px-3 py-3">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {plans.map((plan) => (
                  <tr key={plan.visit.id} className={selectedPlan?.visit.id === plan.visit.id ? 'bg-[#6c3a80]/5' : ''}>
                    <td className="px-3 py-3">
                      <button
                        type="button"
                        onClick={() => setSelectedVisitId(plan.visit.id)}
                        className="text-left font-semibold text-slate-950 hover:text-[#6c3a80]"
                      >
                        {plan.visit.visitNumber}
                      </button>
                      <p className="text-xs text-slate-500">{plan.visit.clientName} - {plan.visit.scheduledStartTime}</p>
                    </td>
                    <td className="px-3 py-3 text-slate-700">{plan.visit.currentCaregiverName}</td>
                    <td className="px-3 py-3">
                      <p className="font-semibold text-slate-950">{plan.recommendedCandidate.caregiverName}</p>
                      <p className="text-xs text-slate-500">score {plan.recommendedCandidate.score}/100</p>
                    </td>
                    <td className="px-3 py-3 text-slate-700">
                      {plan.recommendedCandidate.distanceMiles.toFixed(1)} mi / {plan.recommendedCandidate.travelMinutes} min
                    </td>
                    <td className="px-3 py-3 font-semibold text-emerald-700">{currency.format(plan.savings)}</td>
                    <td className="px-3 py-3">
                      <button
                        type="button"
                        onClick={() => applyRecommendation(plan)}
                        className="rounded-md bg-[#6c3a80] px-3 py-2 text-xs font-semibold text-white transition hover:bg-[#5e3474]"
                      >
                        Use
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-6 w-6 text-[#6c3a80]" />
              <h2 className="text-xl font-bold text-slate-950">Cost and rota alerts</h2>
            </div>
            <div className="mt-5 space-y-3">
              {alerts.slice(0, 8).map((alert) => (
                <div key={alert.id} className={`rounded-lg border p-3 ${alertStyles[alert.level]}`}>
                  <p className="font-semibold">{alert.title}</p>
                  <p className="mt-1 text-sm">{alert.detail}</p>
                </div>
              ))}
              {alerts.length === 0 && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm font-semibold text-emerald-800">
                  No expensive or unsafe scheduling pattern detected.
                </div>
              )}
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <Clock className="h-6 w-6 text-[#6c3a80]" />
              <h2 className="text-xl font-bold text-slate-950">Carer utilisation</h2>
            </div>
            <div className="mt-5 space-y-3">
              {utilisation.map((item) => (
                <div key={item.caregiver.id}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-semibold text-slate-800">{item.caregiver.name}</span>
                    <span className="text-slate-500">{item.hours.toFixed(1)} hrs / {item.utilisation}%</span>
                  </div>
                  <div className="mt-2 h-2 rounded-full bg-slate-100">
                    <div
                      className={`h-2 rounded-full ${item.utilisation > 95 ? 'bg-red-500' : item.utilisation < 45 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                      style={{ width: `${Math.min(100, item.utilisation)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {selectedPlan && (
        <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <div className="flex items-center gap-3">
                <MapPin className="h-6 w-6 text-[#6c3a80]" />
                <h2 className="text-xl font-bold text-slate-950">Candidate scoring for {selectedPlan.visit.clientName}</h2>
              </div>
              <p className="mt-1 text-sm text-slate-600">
                Required skills: {selectedPlan.visit.requiredSkills.join(', ')}. Duration: {selectedPlan.visit.durationMinutes} minutes.
              </p>
            </div>
            <span className="rounded-full bg-[#6c3a80]/10 px-3 py-1 text-sm font-semibold text-[#6c3a80]">
              {selectedPlan.visit.serviceType}
            </span>
          </div>

          <div className="mt-5 grid gap-4 lg:grid-cols-2">
            {selectedCandidates.map((candidate, index) => (
              <div key={candidate.caregiverId} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-[#6c3a80] px-2 py-1 text-xs font-bold text-white">#{index + 1}</span>
                      <h3 className="font-bold text-slate-950">{candidate.caregiverName}</h3>
                    </div>
                    <p className="mt-1 text-sm text-slate-600">
                      {candidate.distanceMiles.toFixed(1)} miles, {candidate.travelMinutes} min travel, cost {currency.format(candidate.estimatedCost)}
                    </p>
                  </div>
                  <span className={`rounded-full px-3 py-1 text-xs font-bold ${candidate.score >= 75 ? 'bg-emerald-100 text-emerald-800' : candidate.score >= 55 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}`}>
                    {candidate.score}/100
                  </span>
                </div>
                <div className="mt-3 space-y-1 text-sm text-slate-700">
                  {candidate.reasons.map((reason) => (
                    <p key={reason} className="flex gap-2">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                      <span>{reason}</span>
                    </p>
                  ))}
                </div>
                {candidate.warnings.length > 0 && (
                  <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                    {candidate.warnings.map((warning) => (
                      <p key={warning}>{warning}</p>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};
